import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { SuiContainer, SuiLabel, SuiTooltip, SuiTextInput, SuiErrorText, SuiHelperText } from '@splashthat/sui';
import { containerPaddingXs } from 'common/styles/variables';

import { getFormNode } from 'common/entities/formNode/selectors';
import { getFormErrors } from 'common/compounds/FormBuilder/selectors';
import { isTemporaryId } from 'common/compounds/FormBuilder/utils/formValidations';
import { updateFormNodeValue } from '../../../actions/nodeActions';
import { EMPTY_CODE, REPEATED_CODE, ALL_ERROR_CODES } from '../../../constants/errorCodes';
import disableWhenLocked from './disableWhenLocked';

const NAME_LABEL = 'Field Name';
const NAME_PLACEHOLDER = 'This will identify your question';
const NAME_TOOLTIP_MESSAGE = 'Question answers will be mapped to this field';
const HIDDEN_INPUT_DESCRIPTION =
    "Field name will appear on integrations and the guest list. It can't be changed after you save.";
const NAME_DISABLED_TOOLTIP_MESSAGE =
    'Field name will appear as a column name on the guest list, a name on the contact card, and in integrations. Field name cannot be changed once a question is saved. To create a new field, add a new question.';
const NAME_EMPTY_ERROR = "Name can't be empty";
const NAME_REPEATED_ERROR = 'This value is already being used by another question';

const errorMessages = {
    [EMPTY_CODE]: NAME_EMPTY_ERROR,
    [REPEATED_CODE]: NAME_REPEATED_ERROR,
};

const FullWidthContainer = styled.div`
    width: 100%;
`;

const PaddedRightLabelWrapper = styled.div`
    padding-right: ${containerPaddingXs};
`;

const FieldNameControl = (props) => {
    const nameErrorMessages = props.errors.map((error) => errorMessages[error]).filter(Boolean);

    const disabled = !isTemporaryId(props.formNodeId) || props.disabled;

    return (
        <FullWidthContainer>
            <SuiContainer variation="flex-horizontal-left">
                <PaddedRightLabelWrapper>
                    <SuiLabel text={NAME_LABEL} />
                </PaddedRightLabelWrapper>
                {!props?.isHiddenInputType && (
                    <SuiTooltip disabled={disabled}>
                        {disabled ? NAME_DISABLED_TOOLTIP_MESSAGE : NAME_TOOLTIP_MESSAGE}
                    </SuiTooltip>
                )}
            </SuiContainer>
            <FullWidthContainer>
                <SuiTextInput
                    fullWidth
                    value={props.code}
                    placeholder={NAME_PLACEHOLDER}
                    onChange={(event, value) => props.onChange(value)}
                    disabled={disabled}
                    variation={nameErrorMessages.length ? 'error' : ''}
                    data-qa="FieldNameControl__input"
                />
                {props?.isHiddenInputType && <SuiHelperText text={HIDDEN_INPUT_DESCRIPTION} />}
            </FullWidthContainer>
            {nameErrorMessages.map((errorMessage) => (
                <SuiErrorText text={errorMessage} key={errorMessage} />
            ))}
        </FullWidthContainer>
    );
};

FieldNameControl.propTypes = {
    formNodeId: PropTypes.string,
    code: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.oneOf([...ALL_ERROR_CODES])),
    isHiddenInputType: PropTypes.bool,
    /** disabled is passed when form node editing is locked */
    disabled: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
};

FieldNameControl.defaultProps = {
    formNodeId: undefined,
    code: '',
    errors: [],
    isHiddenInputType: false,
};

// -------------------
// Connected component

const mapStateToProps = (state, props) => {
    const formNode = getFormNode(state, props.formNodeId);
    return {
        formNodeId: props.formNodeId,
        code: props.formNodeId ? formNode.code : '',
        errors: getFormErrors(state),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    onChange: (code) => {
        dispatch(updateFormNodeValue(ownProps.formNodeId, 'code', code));
        ownProps.onChange(code);
    },
});

const ConnectedFieldNameControl = connect(mapStateToProps, mapDispatchToProps)(disableWhenLocked(FieldNameControl));

ConnectedFieldNameControl.propTypes = {
    formNodeId: PropTypes.string,
    onChange: PropTypes.func,
};
ConnectedFieldNameControl.defaultProps = {
    formNodeId: undefined,
    onChange: () => {},
};

export default ConnectedFieldNameControl;
